/**
 * Assigner
 * 
 * Objects for lead source/category assignment.
 */
import { SessionProfile, SessionProfileAttribute } from "./session";
import { REGEX_FLAGS, regexProblems } from "./regexportability";


interface MatchValue {
    value: string;
    isRegex: boolean;
    isNegative: Boolean;
    // Regex flags, a subset of 'imsu'; absent means none, as before
    // (BACKLOG.md §9.6). The server runs the same pattern through PHP's
    // RegexConverter, so a flag outside that set is refused at save.
    flags?: string;
}

export interface MatchClause {

    matchValues: MatchValue[];
    attribute: SessionProfileAttribute;
    primaryOnly: Boolean;

}

interface MatchAssignment {
    clauses: MatchClause[];
    assignSources: string[];
    assignCategories: string[];
}


export interface MatchConfig {
    version: number;
    assignments: MatchAssignment[];
}


export class Assigner 
{

    public static execute(matchConfig: MatchConfig, sessionProfile: SessionProfile) : SessionProfile
    {
        // clear lead source and category data
        sessionProfile.leadSources = [];
        sessionProfile.leadSourceCategories = [];
        // iterate through assignments and append the sources and categories of
        // EVERY matching assignment, in order. Order decides the lead source,
        // since integrations read only the first (BACKLOG.md §9.16).
        for(let i = 0; i < matchConfig.assignments.length; i ++) {
            let assign = true;
            const assignment = matchConfig.assignments[i];
            for(let j = 0; j < assignment.clauses.length; j ++) {
                const clause = assignment.clauses[j];
                if (!Assigner.checkClause(clause, sessionProfile)) {
                    // move to next assignment
                    assign = false;
                    break;
                }
            }
            if (assign) {
                for(let l = 0; l < assignment.assignSources.length; l ++) {
                    sessionProfile.leadSources.push(assignment.assignSources[l]);
                }
                for(let m = 0; m < assignment.assignCategories.length; m ++) {
                    sessionProfile.leadSourceCategories.push(assignment.assignCategories[m]);
                }    
            }
        }
        return sessionProfile;
    }

    protected static checkClause(clause: MatchClause, sessionProfile: SessionProfile) : boolean
    {
        if (clause.attribute === SessionProfileAttribute.allValues) {
            // no evaluations allowed against the allValues attribute
            return false;
        }
        let rawValues = sessionProfile[clause.attribute];
        if (!Array.isArray(rawValues)) {
            // an attribute this profile does not have: fail the clause closed
            // rather than throw out of Assigner and lose the whole refresh (3.0.0)
            return false;
        }
        if (rawValues.length === 0 && clause.matchValues.length > 0) {
            // no raw values to check against the clause criteria
            return false;
        }
        if (clause.primaryOnly) {
            // @ts-ignore
            rawValues = [rawValues[0]];
        }
        for (let j = 0; j < rawValues.length; j ++) {
            const rawValue = String(rawValues[j]);
            for (let k = 0; k < clause.matchValues.length; k ++) {
                const matchValue = clause.matchValues[k];
                if (matchValue.isRegex) {
                    const regExp = Assigner.compile(matchValue);
                    if (regExp === null) {
                        // cannot run with the meaning the server gives it: fail
                        // closed, negated or not, as the server does (3.0.0, §9.6)
                        return false;
                    }
                    if (!regExp.test(rawValue)) {
                        if (matchValue.isNegative === false) {
                            return false;   
                        }
                    } else {
                        if (matchValue.isNegative === true) {
                            return false;
                        }
                    }
                } else {
                    if (rawValue.toLowerCase() !== matchValue.value.toLowerCase()) {
                        if (matchValue.isNegative === false) {
                            return false;
                        }
                    } else {
                        if (matchValue.isNegative === true) {
                            return false;
                        }
                    }    
                }
            }
        }
        return true;
    }

    protected static compile(matchValue: MatchValue) : RegExp | null
    {
        const flags = matchValue.flags || '';
        if (regexProblems(matchValue.value, flags).length) {
            return null;
        }
        return new RegExp(matchValue.value, flags);
    }

}

/**
 * Throws, naming the first problem, unless `config` is a rule set this library
 * can run exactly as the server does: the structure MatchConfig::fromJson()
 * requires, and every regex accepted by RuleSetValidator on save (3.0.0;
 * BACKLOG.md §9.13 rows 9 and 11, §10). Wrong types are rejected, never cast:
 * `"false"` is truthy. The messages are the server's.
 *
 * One deliberate difference until the server catches up (§9.13 row 9): a clause
 * with no match values is rejected here; PHP still accepts it.
 */
export function validateMatchConfig(config: unknown) : void
{
    const isObject = (v: unknown) => typeof v === 'object' && v !== null && !Array.isArray(v);
    const requireKeys = (v: any, keys: Array<string>, what: string) => {
        const missing = keys.filter((k) => !Object.prototype.hasOwnProperty.call(v, k));
        if (missing.length) {
            throw new Error(`${what} is missing required key(s): ${missing.join(', ')}.`);
        }
    };
    const attributes: Array<string> = Object.keys(SessionProfileAttribute)
        .map((k) => (SessionProfileAttribute as any)[k]);

    if (!isObject(config)) {
        throw new Error('Rule set configuration must be a JSON object.');
    }
    const doc = config as any;
    requireKeys(doc, ['version', 'assignments'], 'Rule set configuration');
    if (!Array.isArray(doc.assignments)) {
        throw new Error("Rule set configuration's 'assignments' must be an array.");
    }
    doc.assignments.forEach((assignment: any, a: number) => {
        if (!isObject(assignment)) {
            throw new Error(`Rule set assignment #${a} must be a JSON object.`);
        }
        requireKeys(assignment, ['assignSources', 'assignCategories', 'clauses'], 'Match assignment');
        if (!Array.isArray(assignment.clauses)) {
            throw new Error("Match assignment's 'clauses' must be an array.");
        }
        ['assignSources', 'assignCategories'].forEach((key) => {
            const list = assignment[key];
            if (!Array.isArray(list) || list.some((v: unknown) => typeof v !== 'string')) {
                throw new Error(`Match assignment's '${key}' must be an array of strings.`);
            }
        });
        assignment.clauses.forEach((clause: any, c: number) => {
            if (!isObject(clause)) {
                throw new Error(`Match clause #${c} must be a JSON object.`);
            }
            requireKeys(clause, ['primaryOnly', 'attribute', 'matchValues'], 'Match clause');
            if (!Array.isArray(clause.matchValues)) {
                throw new Error("Match clause's 'matchValues' must be an array.");
            }
            if (typeof clause.attribute !== 'string') {
                throw new Error("Match clause's 'attribute' must be a string.");
            }
            if (typeof clause.primaryOnly !== 'boolean') {
                throw new Error("Match clause's 'primaryOnly' must be a boolean.");
            }
            if (attributes.indexOf(clause.attribute) === -1) {
                throw new Error(`Match clause has an unknown attribute '${clause.attribute}'.`);
            }
            if (clause.matchValues.length === 0) {
                throw new Error('Match clause has no match values; it would match everything or nothing.');
            }
            clause.matchValues.forEach((matchValue: any, v: number) => {
                if (!isObject(matchValue)) {
                    throw new Error(`Match value #${v} must be a JSON object.`);
                }
                requireKeys(matchValue, ['value', 'isRegex', 'isNegative'], 'Match value');
                if (typeof matchValue.value !== 'string') {
                    throw new Error("Match value's 'value' must be a string.");
                }
                ['isRegex', 'isNegative'].forEach((flag) => {
                    if (typeof matchValue[flag] !== 'boolean') {
                        throw new Error(`Match value's '${flag}' must be a boolean.`);
                    }
                });
                const flags = Object.prototype.hasOwnProperty.call(matchValue, 'flags') ? matchValue.flags : '';
                if (typeof flags !== 'string') {
                    throw new Error("Match value's 'flags' must be a string.");
                }
                if (flags !== '' && !matchValue.isRegex) {
                    throw new Error("Match value's 'flags' apply only to a regex value.");
                }
                const letters = flags.split('');
                const badFlag = letters.some((l: string, i: number) => REGEX_FLAGS.indexOf(l) === -1 || letters.indexOf(l) !== i);
                if (badFlag) {
                    throw new Error(`Match value's 'flags' may hold each of ${REGEX_FLAGS} at most once, not '${flags}'.`);
                }
                if (matchValue.isRegex) {
                    const problems = regexProblems(matchValue.value, flags);
                    if (problems.length) {
                        throw new Error(`Assignment #${a + 1}, clause #${c + 1}, match value #${v + 1}: ${problems[0]}`);
                    }
                }
            });
        });
    });
}
